import { createClient } from '@supabase/supabase-js'

const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

export type Profile = {
  id: string
  role: 'admin' | 'customer' | 'tower' | 'garage' | 'parts_vendor' | 'insurer' // Keep for backward compatibility
  roles?: ('admin' | 'customer' | 'tower' | 'garage' | 'parts_vendor' | 'insurer' | 'vendor')[] // New multi-role support
  full_name: string
  phone: string
  avatar_url: string
  ntsa_certified: boolean
  truck_type: string
  truck_plate: string
  is_online: boolean
  kebs_certified: boolean
  business_name: string
  insurer_name: string
  insurer_since: number | null
  // Phase 2 Garage/Vendor fields
  service_radius_km: number
  bay_capacity: number
  current_bay_usage: number
  specialization: string[]
  verification_status: 'pending' | 'verified' | 'suspended' | 'rejected'
  verification_notes: string
  latitude: number | null
  longitude: number | null
  garage_online: boolean
  payout_schedule: 'daily' | 'weekly' | 'biweekly' | 'monthly'
  commission_rate: number
  auto_matched_count: number
  inventory_updated_at: string
  created_at: string
  updated_at: string
  // Fraud prevention fields
  date_of_birth: string | null
  national_id: string | null
  email: string | null
  physical_address: string | null
  emergency_contact_name: string | null
  emergency_contact_phone: string | null
  id_document_type: string | null
  id_document_number: string | null
  id_document_photo: string | null
  selfie_photo: string | null
  documents?: Record<string, string> // JSONB field for uploaded document URLs
  truck_vin: string | null
  truck_year: number | null
  truck_insurance_provider: string | null
  truck_insurance_policy: string | null
  truck_insurance_expiry: string | null
  roadworthiness_cert: string | null
  ntsa_cert_number: string | null
  ntsa_cert_expiry: string | null
  vehicle_photos: string[] | null
  business_registration: string | null
  tax_pin: string | null
  business_address: string | null
  business_phone: string | null
  business_email: string | null
  business_registration_doc: string | null
  bank_name: string | null
  bank_account_number: string | null
  bank_account_name: string | null
  mpesa_number: string | null
  mpesa_account_name: string | null
  settlement_preference: string | null
  driving_license_number: string | null
  driving_license_expiry: string | null
  commercial_towing_license: string | null
  commercial_towing_expiry: string | null
  first_aid_cert: string | null
  first_aid_expiry: string | null
  kebs_document: string | null
  criminal_background_consent: boolean
  terms_accepted: boolean
  privacy_policy_accepted: boolean
  liability_waiver_accepted: boolean
  insurance_coverage_confirmed: boolean
  registration_submitted_at: string | null
  registration_verified_at: string | null
  registration_verified_by: string | null
  fraud_score: number
  fraud_notes: string | null
  // Tower pricing fields
  base_towing_rate: number
  per_km_rate: number
  battery_service_rate: number
  flat_tyre_rate: number
  fuel_delivery_base_rate: number
  fuel_delivery_per_km_rate: number
  // Parts vendor fields
  parts_specialization: string[]
  onboarding_completed: boolean
  // Team member fields (for business team users)
  team_role?: 'admin' | 'staff'
  team_member_id?: string
  // Additional location fields
  location_city: string | null
  location_county: string | null
  location_area: string | null
  location_link: string | null
}

export type Vehicle = {
  id: string
  owner_id: string
  plate: string
  make: string
  model: string
  year: number | null
  color: string
  insurance_policy_number: string
  insurer_name: string
  policy_active: boolean
  created_at: string
}

export type TowJob = {
  id: string
  reference: string
  customer_id: string
  operator_id: string | null
  vehicle_id: string | null
  vehicle_plate: string | null
  vehicle_make: string | null
  vehicle_model: string | null
  vehicle_color: string | null
  status: 'pending' | 'dispatched' | 'en_route' | 'arrived' | 'towing' | 'completed' | 'delivered_to_garage' | 'payment_pending' | 'payment_completed' | 'cancelled'
  service_type: 'towing' | 'battery' | 'flat_tyre' | 'fuel' | 'parts' | 'insurance_claim'
  service_id: string | null
  pickup_address: string
  pickup_place_name: string | null
  destination_address: string | null
  destination_place_name: string | null
  issue_description: string
  diagnosis_notes: string
  diagnosis_photos: string[]
  payment_method: string
  total_amount: number
  insurer_payout: number
  customer_excess: number
  pickup_lat: number
  pickup_lng: number
  destination_lat: number | null
  destination_lng: number | null
  operator_distance_km: number | null
  insurer_id_selected: string | null
  service_price: number
  fuel_quantity: number | null
  fuel_station_id: string | null
  fuel_price_per_liter: number | null
  fuel_delivery_fee: number | null
  created_at: string
  updated_at: string
  en_route_at: string | null
  completed_at: string | null
  paid_at: string | null
  // Platform payment tracking fields
  platform_paid_to_operator: boolean
  platform_paid_at: string | null
  platform_payment_amount: number
  platform_payment_reference: string
  insurance_claim_generated: boolean
  insurance_claim_id: string | null
  // Garage destination field
  destination_garage_id: string | null
  // Tower selection fields
  selected_tower_id: string | null
  tower_accepted_at: string | null
  tower_declined_at: string | null
  // Additional fields for compatibility
  customer_name: string | null
  customer_phone: string | null
  vendor_id: string | null
  reimbursed_at: string | null
}

export type TowerService = {
  id: string
  tower_id: string
  service_name: string
  description: string | null
  base_price: number
  per_km_rate: number
  requires_destination: boolean
  requires_vehicle: boolean
  is_active: boolean
  vehicle_types: string[] | null
  created_at: string
  updated_at: string
}

export type BusinessUser = {
  id: string
  business_id: string
  user_id: string
  role: 'admin' | 'staff'
  permissions: Record<string, any>
  is_active: boolean
  created_at: string
  updated_at: string
}

export type Claim = {
  id: string
  reference: string
  job_id: string | null
  customer_id: string
  insurer_id: string | null
  policy_holder_name: string
  policy_number: string
  insurer_name: string
  event_description: string
  status: 'pending' | 'approved' | 'paid' | 'rejected' | 'flagged' | 'disputed'
  total_amount: number
  insurer_payout: number
  customer_excess: number
  fraud_flag: boolean
  fraud_notes: string
  approved_at: string | null
  paid_at: string | null
  created_at: string
  updated_at: string
}

export type Part = {
  id: string
  vendor_id: string
  name: string
  description: string
  category: string
  brand: string
  condition: 'new' | 'used' | 'refurbished'
  price: number
  stock: number
  image_url: string
  location: string
  compatible_makes: string
  compatible_models: string
  car_make: string | null
  car_model: string | null
  year_range: string | null
  is_active: boolean
  created_at: string
  updated_at: string
}

export type PartInquiry = {
  id: string
  part_id: string
  customer_id: string
  message: string
  status: 'pending' | 'responded' | 'closed'
  created_at: string
}

export type ChatMessage = {
  id: string
  job_id: string
  sender_id: string
  recipient_id: string
  message: string
  reply_to: string | null
  reply_message: string | null
  reply_sender: string | null
  created_at: string
  read_at: string | null
}

// Phase 2 Garage/Vendor Network Types
export type GarageBay = {
  id: string
  garage_id: string
  bay_number: number
  vehicle_id: string | null
  job_id: string | null
  status: 'available' | 'received' | 'diagnosing' | 'awaiting_parts' | 'in_repair' | 'ready_pickup' | 'maintenance'
  assigned_at: string | null
  updated_at: string
}

export type VehicleTimeline = {
  id: string
  vehicle_id: string
  job_id: string | null
  event_type: 'registration' | 'sos_request' | 'operator_assigned' | 'diagnosis_logged' | 'garage_assigned' | 'bay_status_changed' | 'parts_ordered' | 'payment_completed' | 'job_closed' | 'claim_filed'
  description: string
  metadata: Record<string, any>
  created_at: string
}

export type GarageAssignment = {
  id: string
  job_id: string
  garage_id: string
  status: 'offered' | 'accepted' | 'declined' | 'expired' | 'completed'
  offered_at: string
  responded_at: string | null
  expires_at: string
  distance_km: number
  ranking_score: number
  ranking_factors: Record<string, any>
  notes: string
}

// Parts Order Types (Multi-vendor structure)
export type PartOrder = {
  id: string
  reference: string
  customer_id: string
  status: 'pending' | 'confirmed' | 'processing' | 'partial' | 'ready' | 'delivered' | 'cancelled'
  delivery_method: 'delivery' | 'pickup'
  delivery_address: string
  delivery_lat: number | null
  delivery_lng: number | null
  delivery_notes: string | null
  subtotal: number
  delivery_fee: number
  total_amount: number
  payment_method: 'mpesa' | 'card' | 'cash' | 'bank_transfer' | null
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded'
  paid_at: string | null
  payment_reference: string | null
  estimated_delivery: string | null
  actual_delivery: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

export type PartOrderItem = {
  id: string
  order_id: string
  part_id: string
  vendor_id: string
  quantity: number
  unit_price: number
  total_price: number
  item_status: 'pending' | 'confirmed' | 'processing' | 'ready' | 'delivered' | 'cancelled'
  created_at: string
  updated_at: string
}

export type PartOrderVendorAssignment = {
  id: string
  order_id: string
  vendor_id: string
  status: 'pending' | 'confirmed' | 'processing' | 'ready' | 'delivered' | 'cancelled'
  subtotal: number
  delivery_fee: number
  total_amount: number
  estimated_delivery: string | null
  actual_delivery: string | null
  delivery_notes: string | null
  vendor_payment_status: 'pending' | 'paid' | 'failed' | 'refunded'
  vendor_paid_at: string | null
  notes: string | null
  created_at: string
  updated_at: string
}

// Legacy Order type (kept for compatibility)
export type Order = {
  id: string
  reference: string
  quote_id: string | null
  customer_id: string
  vendor_id: string
  job_id: string | null
  vehicle_id: string | null
  part_id: string | null
  status: 'pending' | 'confirmed' | 'processing' | 'ready' | 'delivered' | 'picked_up' | 'cancelled'
  delivery_method: 'delivery' | 'pickup'
  delivery_address: string
  delivery_lat: number | null
  delivery_lng: number | null
  estimated_delivery: string | null
  actual_delivery: string | null
  subtotal: number
  delivery_fee: number
  total_amount: number
  insurer_amount: number
  customer_amount: number
  payment_method: 'mpesa' | 'card' | 'cash' | 'insurer'
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded'
  paid_at: string | null
  notes: string
  created_at: string
  updated_at: string
}

// Tower-Garage Transaction Types
export type TowerGarageTransaction = {
  id: string
  job_id: string
  tower_id: string
  garage_id: string
  amount: number
  payment_method: 'cash' | 'mpesa' | 'card' | 'bank_transfer'
  payment_reference: string
  document_number: string
  notes: string
  paid_at: string
  created_at: string
  updated_at: string
  auto_generated_ref: boolean
  auto_generated_doc: boolean
}

export type GarageReimbursement = {
  id: string
  garage_id: string
  total_amount: number
  transaction_count: number
  transaction_ids: string[]
  payment_method: 'bank_transfer' | 'mpesa' | 'check'
  payment_reference: string
  reimbursement_period_start: string
  reimbursement_period_end: string
  processed_at: string | null
  processed_by: string | null
  status: 'pending' | 'processing' | 'completed' | 'failed'
  notes: string
  created_at: string
  updated_at: string
}
